import { Link } from "react-router-dom";
import { CalendarClock, MapPin, Video, Users, Pencil, Trash2, Send, RefreshCw, CheckCircle2, XCircle, BellRing } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/api";
import { STATO_INVITO, tipoOf } from "@/lib/calendar";
import { AiEmailButton } from "@/components/ai/AiEmailDialog";
import { ReservedLine } from "@/components/client/ReservedLine";
import { ApptPrivateNote } from "./ApptPrivateNote";

const when = (a) => {
  const s = new Date(a.inizio), e = new Date(a.fine);
  const same = s.toDateString() === e.toDateString();
  return `${format(s, "EEEE d MMMM yyyy, HH:mm", { locale: it })} – ${format(e, same ? "HH:mm" : "d MMM yyyy, HH:mm", { locale: it })}`;
};

const Info = ({ icon: Icon, children, testid }) => (
  <div className="flex items-start gap-3 text-sm text-slate-700" data-testid={testid}><Icon className="h-4 w-4 mt-0.5 text-[#0A1128] shrink-0" /><div className="min-w-0 break-words">{children}</div></div>
);

const reminderText = (a) => {
  const p = a.promemoria || {};
  if (p.stato === "inviato") return [`Promemoria cliente inviato il ${fmtDateTime(p.at)} a ${p.destinatari.join(", ")}`, "text-emerald-700"];
  if (p.stato === "errore") return [`Promemoria cliente non inviato: ${p.errore}`, "text-[#C8102E]"];
  if (p.stato === "nessun_destinatario") return ["Promemoria cliente non inviato: nessun destinatario disponibile", "text-[#C8102E]"];
  if (a.stato_invito === "annullato") return ["Promemoria cliente annullato (appuntamento annullato)", "text-slate-600"];
  const d = new Date(a.inizio); d.setDate(d.getDate() - 1); d.setHours(10, 0, 0, 0);
  if (d < new Date()) return ["Promemoria cliente: orario di invio già passato", "text-slate-600"];
  return [`Promemoria cliente programmato per ${format(d, "EEEE d MMMM 'alle' HH:mm", { locale: it })}`, "text-[#0A1128]"];
};

const ReminderStatus = ({ a }) => {
  if (!a.promemoria_cliente && !a.promemoria?.stato) return null;
  const [text, cls] = reminderText(a);
  return <Info icon={BellRing} testid="appt-reminder-status"><span className={`font-semibold ${cls}`}>{text}</span></Info>;
};

const TIPO_INVIO = { invito: "Invito", aggiornamento: "Aggiornamento", annullamento: "Annullamento" };

const History = ({ items }) => (
  <div className="space-y-2" data-testid="invite-history">
    {[...items].reverse().map((i) => (
      <div key={i.id} className="flex items-start gap-3 rounded-xl bg-slate-50 px-3 py-2.5" data-testid={`invite-history-${i.id}`}>
        {i.status === "inviato" ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> : <XCircle className="h-4 w-4 text-[#E8173A] mt-0.5" />}
        <div className="text-xs text-slate-600 min-w-0">
          <div className="font-bold text-slate-800">{TIPO_INVIO[i.tipo]} · {i.status === "inviato" ? "inviato" : "errore"} · {fmtDateTime(i.sent_at)}</div>
          <div className="truncate">A: {i.to.join(", ")}{i.cc?.length ? ` · CC: ${i.cc.join(", ")}` : ""}</div>
          <div className="text-slate-400">da {i.sent_by_nome}</div>
          {i.error && <div className="text-[#C8102E] font-semibold mt-0.5">{i.error}</div>}
        </div>
      </div>
    ))}
  </div>
);

export const AppointmentDetailDialog = ({ appt: a, onOpenChange, onEdit, onSend, onDelete }) => {
  if (!a) return null;
  const tipo = tipoOf(a.tipo);
  const st = STATO_INVITO[a.stato_invito] || STATO_INVITO.non_inviato;
  const sendKind = a.stato_invito === "da_aggiornare" ? "aggiornamento" : "invito";
  return (
    <Dialog open={!!a} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl core-scroll" data-testid="appointment-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-white rounded-full px-2.5 py-0.5" style={{ background: tipo.color }}>{tipo.label}</span>
            <span className={`text-[11px] font-bold rounded-full border px-2.5 py-0.5 ${st.cls}`} data-testid="appt-invite-status">{st.label}</span>
          </div>
          <DialogTitle className="font-display text-2xl font-black text-[#0A1128] pt-1" data-testid="appt-detail-title">{a.titolo}</DialogTitle>
          <Link to={`/clienti/${a.client_id}`} className="text-sm font-bold text-[#E8173A] hover:underline w-fit" data-testid="appt-detail-client">{a.client_nome}</Link>
          <ReservedLine clientId={a.client_id} testid="appt-detail-reserved" />
        </DialogHeader>
        <div className="space-y-3">
          <Info icon={CalendarClock} testid="appt-detail-when"><span className="inline-block first-letter:uppercase">{when(a)}</span></Info>
          {a.luogo && <Info icon={MapPin} testid="appt-detail-luogo">{a.luogo}</Info>}
          {a.link && <Info icon={Video} testid="appt-detail-link"><a href={a.link} target="_blank" rel="noreferrer" className="font-semibold text-[#0A1128] hover:text-[#E8173A]">{a.link}</a></Info>}
          {a.partecipanti_nomi?.length > 0 && <Info icon={Users} testid="appt-detail-soci">{a.partecipanti_nomi.join(", ")}</Info>}
          {a.descrizione && <div className="text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 rounded-xl p-3">{a.descrizione}</div>}
        </div>
        <ReminderStatus a={a} />
        {a.stato_invito === "da_aggiornare" && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="appt-update-pending">
            Hai modificato data, ora o luogo dopo l'invio dell'invito: invia un aggiornamento ai destinatari.
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {a.stato_invito !== "annullato" && (
            <Button onClick={() => onSend(a, sendKind)} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="appt-send-invite-button">
              {sendKind === "aggiornamento" ? <RefreshCw className="h-4 w-4 mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
              {sendKind === "aggiornamento" ? "Invia aggiornamento" : a.stato_invito === "inviato" ? "Invia di nuovo" : "Invia invito"}
            </Button>
          )}
          <Button variant="outline" onClick={() => onEdit(a)} className="rounded-full font-bold text-[#0A1128]" data-testid="appt-edit-button"><Pencil className="h-4 w-4 mr-1.5" />Modifica</Button>
          <Button variant="outline" onClick={() => onDelete(a)} className="rounded-full font-bold text-[#E8173A] border-red-200 hover:bg-red-50 hover:text-[#E8173A]" data-testid="appt-delete-button"><Trash2 className="h-4 w-4 mr-1.5" />Elimina</Button>
          <AiEmailButton client={{ id: a.client_id, ragione_sociale: a.client_nome, email: a.client_email }} appointment={a} />
        </div>
        {a.inviti?.length > 0 && (
          <div className="pt-2">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Storico inviti</div>
            <History items={a.inviti} />
          </div>
        )}
        <ApptPrivateNote apptId={a.id} />
      </DialogContent>
    </Dialog>
  );
};
